import _ from 'lodash';
import React, {FC, Key, useCallback} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {reportToBugsnag} from '../../../../../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../../../../../srcCommon/services/logger/logger';
import {ConversationStatusesEnum} from '../../../../../helpers/models/conversationHelpers';
import {isDraftMessage, parseEmailMessageBody} from '../../../../../helpers/models/messageHelpers';
import {ReplyTypesEnum} from '../../../../../helpers/models/recipientHelpers';
import {useAppDispatch} from '../../../../../helpers/reduxHelpers';
import {
  AppParametricSelector,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../helpers/reselectHelpers';
import {ActivityVisualTypesEnum} from '../../../../../models/api/activityModel';
import {AnnotationModel} from '../../../../../models/api/annotationModel';
import {ChannelTypesEnum} from '../../../../../models/api/channelCommonTypes';
import {ChannelModel} from '../../../../../models/api/channelModel';
import {RHighlightMessageAnnotationComment} from '../../../../../models/api/commentModel';
import {ConversationActionContextsEnum} from '../../../../../models/api/conversationCommonTypes';
import {
  MessageModel,
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
  MessageVisualTypesEnum,
  SendActionsEnum,
  SendMessageOptionsModel,
} from '../../../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../models/api/recipientModel';
import {httpToken} from '../../../../../services/http/httpBase';
import {openMessageRenderPopup} from '../../../../../services/popups';
import {deleteMessageTask} from '../../../../../services/tasks/deleteMessageTask';
import {ReplyLocationsEnum} from '../../../../../services/tracking';
import {AppState} from '../../../../../store/appState';
import {getChannels} from '../../../../../store/data/channels/channelsSelectors';
import {highlightInMessageAction} from '../../../../../store/data/comments/highlightActions';
import {makeGetVisualRecipientMap} from '../../../../../store/data/contacts/recipientsSelectors';
import {setConversationsStatusAction} from '../../../../../store/data/conversations/statusConversationsActions';
import {
  getActivities,
  getChatUsersOnline,
  getComments,
  getConversations,
  getMessages,
  getReferencingCommentUidsMap,
} from '../../../../../store/data/dataSelectors';
import {fetchIntegrationTypesAsyncAction} from '../../../../../store/data/integrationTypes/integrationTypesActions';
import {cancelMessageAsyncAction} from '../../../../../store/data/messages/cancelMessageAction';
import {
  cloneMessage,
  replyToMessageAction,
  stealSharedDraft,
} from '../../../../../store/data/messages/draftMessagesActions';
import {
  makeGetAuthor,
  makeGetIsMessageVisuallyShared,
  makeGetIsUserAuthor,
  makeGetMessage,
  makeGetMessageActivitySourceVisualType,
  makeGetRelatedIntegration,
} from '../../../../../store/data/messages/messagesSelectors';
import {sendMessageAsyncAction} from '../../../../../store/data/messages/sendMessageActions';
import {
  getDefaultReplyType,
  getShouldHideExternalImages,
  getShouldReplyInNewConversation,
} from '../../../../../store/data/preferences/preferencesSelectors';
import {getIsUserVisitor} from '../../../../../store/data/teammates/teammatesSelectors';
import {
  getHasAutoShareDrafts,
  getHasChatPresenceIndicatorsOnOffLine,
  getHasNewChatSeenIndicator,
  getHasNewVisitorUrlExperience,
} from '../../../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {EmlSource} from '../../../../../store/panels/eml/emlPanelState';
import {popoutMessageViewerAction} from '../../../../../store/panels/message/messageViewerPanelActions';
import {
  getPanels,
  makeGetIsMessagePopout,
  makeGetIsMessagePopup,
} from '../../../../../store/panels/panelsSelectors';
import {
  EmlPanelId,
  MessagePanelId,
  PanelTypesEnum,
  REmlPanelId,
  RViewerPanelId,
  ViewerPanelId,
} from '../../../../../store/panels/panelStateBase';
import {
  collapseActivityViewerAction,
  expandActivityViewerAsyncAction,
} from '../../../../../store/panels/viewer/activity/activityViewerActions';
import {ActivityViewerState} from '../../../../../store/panels/viewer/activity/activityViewerState';
import {ViewerPanelPageTypesEnum} from '../../../../../store/panels/viewer/viewerPanelState';
import {MessageViewer} from './messageViewer';
import {useAutoShareDraftAlert} from './useAutoShareDraftAlert';

/*
 * Props.
 */

interface MessageViewerContainerCommonProps {
  isFirstMessage?: boolean;
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
  cellsContext: CellsContext;
  namespace: string;
  conversationId: number | undefined;
  conversationActionContext: ConversationActionContextsEnum | undefined;
  isSpam: boolean;
  activityId?: number;
  state?: ActivityViewerState;
  previousMessageUid?: string;
  replyChannel?: ChannelModel;
  emlSource?: EmlSource;
  isAuthorReplying?: boolean;
  isSharedDraft?: boolean;
  jumpKey?: Key;
  lastIncomeMessageUid?: string;
}

interface MessageViewerContainerBaseProps extends MessageViewerContainerCommonProps {
  message: MessageModel;
}

/*
 * Selectors.
 */

/** Extract the current page from the props. */
function getState(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.state;
}

/** Extract the conversation from the props. */
function getNamespace(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.namespace;
}

/** Extract the messageUid of the previous activity in the timeline, if available. */
function getPreviousMessageUid(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.previousMessageUid;
}

function getLastIncomeMessageUidMessageUid(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.lastIncomeMessageUid;
}

function getMessage(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.message;
}

function getActivityId(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.activityId;
}

function getIsSpam(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.isSpam;
}

function getPanelId(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.panelId;
}

function getIsSharedDraft(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.isSharedDraft;
}

function makeGetIsExpanded() {
  return createSelector(getState, (state) => {
    if (!state || state.type !== ActivityVisualTypesEnum.MESSAGE) {
      return true;
    }

    return state.isExpanded;
  });
}

function getConversationId(_state: AppState, props: MessageViewerContainerBaseProps) {
  return props.message.conversationId;
}

function getChatChannelFromConversationId(state: AppState, props: MessageViewerContainerBaseProps) {
  const {conversationId} = props;

  if (!conversationId) {
    return null;
  }

  const conversationsById = getConversations(state);
  const channelIds = conversationsById[conversationId]?.channelIds ?? [];

  // caveat - conversations generated from a chat channel only have 1 channel,
  // but this is not guaranteed for conversations coming from other channels.
  const channelsById = getChannels(state);
  const chatChannels = channelIds
    .map((channelId) => channelsById[channelId])
    .filter((channel) => channel?.type === ChannelTypesEnum.FRONT_CHAT);

  return chatChannels[0];
}

function makeGetHideTeammateNames() {
  return createSelector(getChatChannelFromConversationId, (chatChannel) => {
    if (!chatChannel || chatChannel.type !== ChannelTypesEnum.FRONT_CHAT) {
      return undefined;
    }

    return chatChannel.preferences.hideTeammateDetails.hideTeammateNames;
  });
}

function makeGetWidgetCompanyName() {
  return createSelector(getChatChannelFromConversationId, (chatChannel) => {
    if (!chatChannel || chatChannel.type !== ChannelTypesEnum.FRONT_CHAT) {
      return undefined;
    }

    return chatChannel.preferences.widgetCompanyName;
  });
}

function makeGetChatUserName() {
  return createSelector([getMessages, getLastIncomeMessageUidMessageUid], (messages, messageUid) => {
    if (!messageUid) {
      return undefined;
    }

    const message = messages[messageUid];
    const chatUserRecipient = message?.recipients.find(
      (recipient) => recipient.role === RecipientRolesEnum.FROM,
    );

    return chatUserRecipient?.name;
  });
}

/** Find the message for to the previous activity, if available. */
function makeGetPreviousMessage() {
  return createSelector([getMessages, getPreviousMessageUid], (messages, messageUid) => {
    if (!messageUid) {
      return undefined;
    }

    // Find the message for this activity.
    const message = messages[messageUid];
    if (!message) {
      reportToBugsnag(new Error('A message could not be found for this activity.'));
      return undefined;
    }

    return message;
  });
}

function makeGetIsPreviousActivityMessage(getPreviousMessage: ReturnType<typeof makeGetPreviousMessage>) {
  return createSelector(getPreviousMessage, (previousMessage) => Boolean(previousMessage));
}

/** Check if message is a chat follow-up: another chat message from the same author. */
function makeGetIsChatFollowUp<P>(getPreviousMessage: AppParametricSelector<P, MessageModel | undefined>) {
  return createSelector([getMessage, getPreviousMessage], (message, previousMessage) => {
    if (
      !previousMessage ||
      message.visualType !== MessageVisualTypesEnum.CHAT ||
      previousMessage.visualType !== MessageVisualTypesEnum.CHAT
    ) {
      return false;
    }

    if (message.authorId || previousMessage.authorId) {
      return message.authorId === previousMessage.authorId;
    }

    const fromRecipient = message.recipients.find((r) => r.role === RecipientRolesEnum.FROM);
    const previousFromRecipient = previousMessage.recipients.find((r) => r.role === RecipientRolesEnum.FROM);

    if (!fromRecipient || !previousFromRecipient) {
      return false;
    }

    // Check if the sender is the same and has the same name. Custom channels may reuse the same
    // contact/handle with a different name.
    return (
      fromRecipient.contactUid === previousFromRecipient.contactUid &&
      fromRecipient.name === previousFromRecipient.name
    );
  });
}

function makeGetCanHideExternalContent() {
  return createSelector(
    [getIsSpam, getShouldHideExternalImages],
    (isSpam, shouldHideExternalImages) => isSpam || shouldHideExternalImages,
  );
}

function makeGetSharedDraftInReplyToUid() {
  return createSelector(
    [getPanelId, getPanels, getMessage, getIsSharedDraft, getActivities],
    (panelId, panels, message, isSharedDraft, activities) => {
      // If the panel isn't a viewer panel or if we don't have a message, nothing to do.
      const panel = panels[panelId];
      if (!panel || panel.type !== PanelTypesEnum.VIEWER || !message) {
        return undefined;
      }

      // If the current message isn't a shared draft to begin with, nothing to do.
      if (message.visualStatus !== MessageVisualStatusesEnum.DRAFT || !isSharedDraft) {
        return undefined;
      }

      // If we don't have a inReplyToId, nothing to do.
      const {inReplyToId} = message;
      if (!inReplyToId) {
        return undefined;
      }

      // If we don't have any activities, nothing to do.
      const page = panel.pages.current ?? panel.pages.next;
      const conversationPage = page.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ? page : undefined;
      const activitiesFromPage = conversationPage?.activities;
      if (!activitiesFromPage) {
        return undefined;
      }

      const inReplyToActivity = _(activitiesFromPage || [])
        .map((activity) => activity && activity.type === ActivityVisualTypesEnum.MESSAGE && activity)
        .compact()
        .map((messageActivity) => activities[messageActivity.activityId])
        .compact()
        .find(
          (activity) =>
            activity.visualType === ActivityVisualTypesEnum.MESSAGE && activity.messageId === inReplyToId,
        );

      // If we didn't find a message activity corresponding to the inReplyToId, we stop here.
      if (!inReplyToActivity || inReplyToActivity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
        return undefined;
      }

      // Otherwise let's return the message uid to be used later when stealing the draft.
      return inReplyToActivity.messageUid;
    },
  );
}

function makeGetHighlightAnnotations() {
  return createSelector(
    [getActivityId, getComments, getReferencingCommentUidsMap],
    (activityId, comments, referencingCommentUidsMap) => {
      const referencingCommentUids = (activityId && referencingCommentUidsMap[activityId]) || [];

      return _(referencingCommentUids)
        .map((uid) => {
          const comment = comments[uid];
          if (!comment || !RHighlightMessageAnnotationComment.is(comment)) {
            return undefined;
          }

          return comment.annotation;
        })
        .compact()
        .sortBy((annotation) => annotation.startIndex)
        .value();
    },
  );
}

function makeGetHasChatUsersOnline() {
  return createSelector(
    [getConversationId, getChatUsersOnline],
    (conversationId, chatUsersOnline) => chatUsersOnline[conversationId] || false,
  );
}

/*
 * Component.
 */

type MessageViewerStateProps = ResolveStateProps<typeof mapStateToBaseProps>;
type MessageViewerContainerBaseComponentProps = MessageViewerContainerBaseProps & MessageViewerStateProps;

const MessageViewerContainerBaseComponent: FC<MessageViewerContainerBaseComponentProps> = (props) => {
  const dispatch = useAppDispatch();

  const {
    panelId,
    conversationId,
    activityId,
    cellsContext,
    message,
    sharedDraftInReplyToUid,
    conversationActionContext,
  } = props;
  const {shouldReplyInNewConversation, isInMessagePopup} = props;
  const {maybeRegisterHandler, maybeRenderDialog} = useAutoShareDraftAlert(conversationId);

  useInitialEffect(() => {
    dispatch(fetchIntegrationTypesAsyncAction());
  });

  const onExpandClick = useCallback(() => {
    if (!RViewerPanelId.is(panelId) || !activityId) {
      return;
    }

    dispatch(expandActivityViewerAsyncAction(panelId, activityId));
  }, [dispatch, panelId, activityId]);

  const onCollapseClick = useCallback(() => {
    if (!RViewerPanelId.is(panelId) || !activityId) {
      return;
    }

    dispatch(collapseActivityViewerAction(panelId, activityId));
  }, [dispatch, panelId, activityId]);

  const onReplyClick = useCallback(
    (location: ReplyLocationsEnum) => {
      maybeRegisterHandler(() => {
        dispatch(
          replyToMessageAction({
            panelId,
            conversationId,
            replyToUid: message.uid,
            replyType: ReplyTypesEnum.SINGLE,
            location,
            shouldCreateConversation: shouldReplyInNewConversation,
            shouldPopout: isInMessagePopup,
            overrides: {message},
          }),
        );
      });
    },
    [
      maybeRegisterHandler,
      dispatch,
      panelId,
      conversationId,
      message,
      shouldReplyInNewConversation,
      isInMessagePopup,
    ],
  );

  const onReplyAllClick = useCallback(
    (location: ReplyLocationsEnum) => {
      maybeRegisterHandler(() => {
        dispatch(
          replyToMessageAction({
            panelId,
            conversationId,
            replyToUid: message.uid,
            replyType: ReplyTypesEnum.ALL,
            location,
            shouldCreateConversation: shouldReplyInNewConversation,
            shouldPopout: isInMessagePopup,
            overrides: {message},
          }),
        );
      });
    },
    [
      maybeRegisterHandler,
      dispatch,
      panelId,
      conversationId,
      message,
      shouldReplyInNewConversation,
      isInMessagePopup,
    ],
  );

  const onForwardClick = useCallback(
    (location: ReplyLocationsEnum, shouldCreateConversation?: boolean) => {
      dispatch(
        replyToMessageAction({
          panelId,
          conversationId,
          replyToUid: message.uid,
          replyType: ReplyTypesEnum.FORWARD,
          location,
          shouldCreateConversation: shouldCreateConversation || shouldReplyInNewConversation,
          shouldPopout: isInMessagePopup,
          overrides: {message},
        }),
      );
    },
    [dispatch, panelId, conversationId, message, shouldReplyInNewConversation, isInMessagePopup],
  );

  const onCloneClick = useCallback(
    (location: ReplyLocationsEnum, shouldCreateConversation: boolean) => {
      if (!conversationId || REmlPanelId.is(panelId)) {
        return;
      }

      dispatch(
        cloneMessage(
          {
            panelId,
            conversationId,
            originalMessageUid: message.uid,
            location,
            shouldCreateConversation: shouldCreateConversation || shouldReplyInNewConversation,
            shouldPopout: isInMessagePopup,
          },
          parseEmailMessageBody,
        ),
      );
    },
    [dispatch, panelId, conversationId, message, shouldReplyInNewConversation, isInMessagePopup],
  );

  const onDestroyConversationClick = useCallback(() => {
    if (!conversationId) {
      return;
    }

    dispatch(
      setConversationsStatusAction(
        cellsContext,
        [conversationId],
        ConversationStatusesEnum.DESTROYED,
        () => conversationActionContext,
        {
          preventUndo: true,
        },
      ),
    );
  }, [dispatch, conversationId, cellsContext, conversationActionContext]);

  const onCancelSendClick = useCallback(() => {
    dispatch(cancelMessageAsyncAction(message.uid));
  }, [dispatch, message]);

  const onStealDraftClick = useCallback(() => {
    dispatch(stealSharedDraft(message.uid, sharedDraftInReplyToUid));
  }, [dispatch, message, sharedDraftInReplyToUid]);

  const onRetryFailedDraftClick = useCallback(() => {
    // Only a failed draft can be retried.
    if (
      message.visualStatus !== MessageVisualStatusesEnum.DRAFT ||
      message.status !== MessageStatusesEnum.DRAFT_FAILED
    ) {
      return;
    }

    // The message may have embedded send options.
    const sendOptions: SendMessageOptionsModel = message.sendOptions || {
      sendAction: SendActionsEnum.SEND,
      cellsContext,
    };

    // This shouldn't happen, but if it does then conflict resolution is broken and we may have data loss.
    if (!message.version) {
      logger.error('Failed draft with missing version', {message_uid: message.uid});
    }

    // Send the message as displayed.
    dispatch(
      sendMessageAsyncAction(message.uid, sendOptions, {
        getVersion: () => message.version,
      }),
    );
  }, [dispatch, message, cellsContext]);

  const onDeleteDraftClick = useCallback(() => {
    // Only a failed draft can be deleted.
    if (
      message.visualStatus !== MessageVisualStatusesEnum.DRAFT ||
      message.status !== MessageStatusesEnum.DRAFT_FAILED
    ) {
      return;
    }

    dispatch(deleteMessageTask(message.uid));
  }, [dispatch, message]);

  const onOpenMessageRenderClick = useCallback(() => {
    if (message.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
      return;
    }

    openMessageRenderPopup(message);
  }, [message]);

  const onPopoutClick = useCallback(
    (initialHeight?: number) => {
      dispatch(
        popoutMessageViewerAction(panelId, message.uid, {
          cellsContext,
          initialHeight,
        }),
      );
    },
    [dispatch, panelId, message.uid, cellsContext],
  );

  const onQuoteInCommentClick = useCallback(
    (annotation: AnnotationModel) => {
      if (!conversationId || !activityId) {
        return;
      }

      dispatch(highlightInMessageAction(message, annotation, activityId));
    },
    [conversationId, activityId, dispatch, message],
  );

  return (
    <>
      {maybeRenderDialog()}
      <MessageViewer
        {...props}
        activityId={props.activityId}
        httpToken={httpToken()}
        onExpandClick={onExpandClick}
        onCollapseClick={onCollapseClick}
        onReplyClick={onReplyClick}
        onReplyAllClick={onReplyAllClick}
        onForwardClick={onForwardClick}
        onCloneClick={onCloneClick}
        onDestroyConversationClick={onDestroyConversationClick}
        onCancelSendClick={onCancelSendClick}
        onStealDraftClick={onStealDraftClick}
        onRetryFailedDraftClick={onRetryFailedDraftClick}
        onDeleteDraftClick={onDeleteDraftClick}
        onOpenMessageRenderClick={onOpenMessageRenderClick}
        onPopoutClick={onPopoutClick}
        onQuoteInCommentClick={onQuoteInCommentClick}
      />
    </>
  );
};

function makeGetHasMessageAnnotations() {
  return createSelector(getMessage, (message) => !isDraftMessage(message));
}

/*
 * Redux.
 */

const mapStateToBaseProps = makeMapStateToProps<MessageViewerContainerBaseProps>()(() => {
  const getPreviousMessage = makeGetPreviousMessage();
  const getAuthor = makeGetAuthor(getMessage);
  const getVisualRecipients = makeGetVisualRecipientMap(getNamespace, getMessage);
  const getIsExpanded = makeGetIsExpanded();
  const getIsUserAuthor = makeGetIsUserAuthor(getAuthor);
  const getIsPreviousActivityMessage = makeGetIsPreviousActivityMessage(getPreviousMessage);
  const getIsChatFollowUp = makeGetIsChatFollowUp(getPreviousMessage);
  const getCanHideExternalContent = makeGetCanHideExternalContent();
  const getRelatedIntegration = makeGetRelatedIntegration(getMessage);
  const getIsChatUserOnline = makeGetHasChatUsersOnline();
  const getChatUserName = makeGetChatUserName();

  return {
    author: getAuthor,
    hideTeammateNames: makeGetHideTeammateNames(),
    widgetCompanyName: makeGetWidgetCompanyName(),
    visualRecipients: getVisualRecipients,
    annotations: makeGetHighlightAnnotations(),
    isExpanded: getIsExpanded,
    isUserVisitor: getIsUserVisitor,
    isUserAuthor: getIsUserAuthor,
    activitySourceVisualType: makeGetMessageActivitySourceVisualType(getActivityId),
    defaultReplyType: getDefaultReplyType,
    isPreviousActivityMessage: getIsPreviousActivityMessage,
    isChatFollowUp: getIsChatFollowUp,
    isChatUserOnline: getIsChatUserOnline,
    hasChatUserOnlineFeatureFlag: getHasChatPresenceIndicatorsOnOffLine,
    shouldReplyInNewConversation: getShouldReplyInNewConversation,
    canHideExternalContent: getCanHideExternalContent,
    isInMessagePopup: makeGetIsMessagePopup(getPanelId),
    isInMessagePopout: makeGetIsMessagePopout(getPanelId),
    relatedIntegration: getRelatedIntegration,
    sharedDraftInReplyToUid: makeGetSharedDraftInReplyToUid(),
    hasMessageAnnotations: makeGetHasMessageAnnotations(),
    hasAutoShareDrafts: getHasAutoShareDrafts,
    isMessageShared: makeGetIsMessageVisuallyShared(getMessage),
    chatUserName: getChatUserName,
  };
});

export const MessageViewerContainerBase = connect(mapStateToBaseProps)(MessageViewerContainerBaseComponent);

interface MessageViewerContainerProps extends MessageViewerContainerCommonProps {
  messageUid: string;
}

function getMessageUid(_state: AppState, props: MessageViewerContainerProps) {
  return props.messageUid;
}

const mapStateToProps = makeMapStateToProps<MessageViewerContainerProps>()(() => ({
  message: makeGetMessage(getMessageUid),
  hasNewVisitorUrlExperience: getHasNewVisitorUrlExperience,
  hasNewChatSeenIndicator: getHasNewChatSeenIndicator,
}));

export const MessageViewerContainer = connect(mapStateToProps)(MessageViewerContainerBase);
